import { BaseTrack, TrackConfig, TrackData } from '../base/base-track';

const RSCAPE_COLOR = '#27AE60';   // green for covariation positions
const RSCAPE_EMPTY = '#FFFFFF';   // white for non-covariation positions

/**
 * Apply per-position colouring to a nightingale-sequence element based on an
 * R-scape annotation string (characters: '.' = no covariation, '2' = covariation).
 *
 * nightingale-sequence renders to light DOM so querySelector works directly.
 * D3 binds position data to each rect as element.__data__ = { position, aa }
 * where position is 1-indexed.
 */
function applyRscapeColors(seqElement: HTMLElement, rscape: string, hideText = false): void {
  const bg = seqElement.querySelector('.background');
  if (!bg) return;

  bg.querySelectorAll('rect.base_bg').forEach((rect) => {
    const data = (rect as any).__data__ as { position: number } | undefined;
    if (!data) return;
    const char = rscape[data.position - 1];
    (rect as SVGElement).setAttribute('fill', char === '2' ? RSCAPE_COLOR : RSCAPE_EMPTY);
    (rect as any).style.opacity = '1';
  });

  if (hideText) {
    seqElement.querySelectorAll('text.base').forEach((t) => {
      (t as SVGElement).setAttribute('visibility', 'hidden');
    });
  }
}

export function hookRscapeColoring(
  seqElement: any,
  rscape: string,
  hideText = false,
): void {
  const apply = () => applyRscapeColors(seqElement, rscape, hideText);

  // Patch zoomRefreshed so colours survive every pan/zoom re-render.
  const orig = seqElement.zoomRefreshed?.bind(seqElement);
  seqElement.zoomRefreshed = () => {
    orig?.();
    apply();
  };

  // Also apply immediately (the first render may have already happened).
  apply();
}

export class RscapeTrack extends BaseTrack {
  constructor(config: TrackConfig, data: TrackData) {
    super('rscape', { ...config, height: 30 }, data);
  }

  createHTML(): string {
    if (!this.hasRequiredData() || !this.data.notation?.rscape) return '';

    return `
      <div style="display: flex; align-items: center;">
        <div style="width: ${this.config.labelWidth}px; flex-shrink: 0;">
          Covarying base pairs
        </div>
        <nightingale-sequence id="rscape-track"></nightingale-sequence>
      </div>
    `;
  }

  setup(container: HTMLElement): void {
    this.element = container.querySelector('#rscape-track') as HTMLElement;
    if (!this.element) return;
    this.setupRscapeElement();
  }

  update(config?: Partial<TrackConfig>, data?: Partial<TrackData>): void {
    if (config) this.config = { ...this.config, ...config };
    if (data) this.data = { ...this.data, ...data };
    if (this.element) this.setupRscapeElement();
  }

  destroy(): void {
    this.element = undefined;
  }

  private setupRscapeElement(): void {
    const rscape = this.data.notation?.rscape;
    if (!this.element || !rscape) return;

    this.applyAttributes(this.element, {
      ...this.getNightingaleAttributes(),
      'height': '30',
      'font-size': '14',
      'font-family': 'Courier New, Monaco, Lucida Console, monospace',
      'margin-top': '2',
      'margin-bottom': '2',
    });

    // Set the rscape string as the sequence; styling is applied via hookRscapeColoring
    (this.element as any).data = { name: 'R-scape', sequence: rscape };

    // Hook in after data is set (zoomRefreshed fires on data change)
    hookRscapeColoring(this.element, rscape, true /* hide dot characters */);
  }
}
